import { Link } from 'react-router-dom';

interface Service {
  name: string;
  tagline: string;
  description: string;
  image: string;
  detail: string;
  icon: string;
}

interface ServicesGridProps {
  title?: string;
  subtitle?: string;
}

const services: Service[] = [
  {
    name: 'Chain-Link Fencing',
    tagline: 'Security & Durability',
    description: 'Galvanized steel security systems precision-installed for residential and commercial properties. Maximum protection with minimal maintenance demands.',
    image: 'https://public.readdy.ai/ai/img_res/f1beb286b5f1e17088259389ad74f6a1.jpg',
    detail: 'Galvanized Steel · Custom Heights · Rust-Resistant',
    icon: 'fa-link',
  },
  {
    name: 'Wood Privacy Fence',
    tagline: 'Natural Beauty & Seclusion',
    description: 'Hand-crafted board-on-board cedar fencing delivering complete privacy, timeless natural warmth, and meaningful property value uplift.',
    image: 'https://readdy.ai/api/search-image?query=Masterfully%20crafted%20board-on-board%20cedar%20wood%20privacy%20fence%20in%20luxurious%20backyard%2C%20deep%20rich%20warm%20honey-brown%20wood%20tones%20with%20visible%20natural%20grain%2C%20perfectly%20uniform%20vertical%20boards%2C%20lush%20emerald%20lawn%20in%20foreground%2C%20vibrant%20flowering%20garden%20border%2C%20soft%20dappled%20golden%20sunlight%20streaming%20from%20right%2C%20high-end%20residential%20estate%20Georgia%2C%20premium%20lifestyle%20photography%2C%20shallow%20depth%20of%20field%20focus%20on%20fence&width=800&height=600&seq=mf_v3_wood&orientation=landscape',
    detail: 'Premium Cedar & Pine · Board-on-Board · Staining',
    icon: 'fa-tree',
  },
  {
    name: 'Steel Fence',
    tagline: 'Strength & Modern Elegance',
    description: 'Ornamental powder-coated steel delivering uncompromising durability and bold architectural presence — from estate entrances to commercial perimeters.',
    image: 'https://readdy.ai/api/search-image?query=Sophisticated%20matte%20black%20powder-coated%20ornamental%20steel%20fence%20with%20elegant%20spear%20top%20finials%20at%20luxury%20Southern%20estate%2C%20manicured%20green%20lawn%20visible%20through%20decorative%20vertical%20bars%2C%20grand%20home%20architecture%20softly%20blurred%20in%20warm%20golden%20background%20light%2C%20prestigious%20curb%20appeal%2C%20cinematic%20real%20estate%20photography%2C%20afternoon%20golden%20hour%20glow&width=800&height=600&seq=mf_v3_steel&orientation=landscape',
    detail: 'Powder-Coated · Ornamental Styles · Commercial Grade',
    icon: 'fa-shield-halved',
  },
  {
    name: 'Vinyl Fence',
    tagline: 'Zero Maintenance, Maximum Curb Appeal',
    description: "Weatherproof vinyl that resists rot, UV fading, and Georgia's intense humidity. A pristine, clean look that lasts decades without a single brush of paint.",
    image: 'https://readdy.ai/api/search-image?query=Immaculate%20bright%20white%20vinyl%20privacy%20fence%20panels%20in%20upscale%20suburban%20Georgia%20backyard%2C%20smooth%20clean%20surface%20with%20no%20imperfections%2C%20perfectly%20aligned%20posts%2C%20vivid%20emerald%20green%20grass%20in%20foreground%2C%20colorful%20perennial%20flower%20garden%20border%2C%20brilliant%20sunny%20afternoon%20sky%2C%20new%20construction%20luxury%20home%20exterior%20partially%20visible%2C%20premium%20lifestyle%20photography&width=800&height=600&seq=mf_v3_vinyl&orientation=landscape',
    detail: 'Rot & UV Resistant · Privacy & Ranch Styles',
    icon: 'fa-border-all',
  },
  {
    name: 'Aluminum Fence',
    tagline: 'Timeless Elegance, Built to Last',
    description: 'Rust-proof aluminum with architectural detail — the ideal frame for luxury pools, estates, and properties where curb appeal is non-negotiable.',
    image: 'https://readdy.ai/api/search-image?query=Refined%20dark%20bronze%20powder-coated%20aluminum%20decorative%20fence%20surrounding%20resort-style%20pool%20at%20luxury%20Southern%20estate%2C%20elegant%20architectural%20railing%20design%2C%20crystal%20clear%20turquoise%20pool%20water%20shimmering%2C%20tropical%20palm%20trees%20and%20lush%20landscaping%2C%20dramatic%20late%20afternoon%20light%20casting%20long%20shadows%2C%20high-end%20resort%20photography%2C%20aspirational%20lifestyle&width=800&height=600&seq=mf_v3_alum&orientation=landscape',
    detail: 'Rust-Free · Pool-Safe · Decorative Styles',
    icon: 'fa-award',
  },
  {
    name: 'Custom Gates',
    tagline: 'The Grand Entrance Your Property Deserves',
    description: 'Slide, swing, ornamental, and automated gate systems — custom-designed and built to complete your fence perimeter with a statement entrance.',
    image: 'https://readdy.ai/api/search-image?query=Magnificent%20custom%20wrought%20iron%20ornamental%20driveway%20gate%20at%20grand%20luxury%20estate%20entrance%2C%20matte%20black%20powder-coated%20double%20swing%20gate%20with%20decorative%20scrollwork%2C%20imposing%20stone%20masonry%20pillar%20columns%20flanking%20both%20sides%2C%20tree-lined%20private%20driveway%20leading%20to%20mansion%2C%20warm%20amber%20sunset%20sky%2C%20deep%20shadows%20and%20rich%20contrast%2C%20premium%20estate%20photography%2C%20dramatic%20perspective&width=800&height=600&seq=mf_v3_gate&orientation=landscape',
    detail: 'Slide · Swing · Ornamental · Automated',
    icon: 'fa-door-open',
  },
];

const ServicesGrid: React.FC<ServicesGridProps> = ({
  title = 'Six Premium Fencing Solutions',
  subtitle = 'Every fence we build reflects a commitment to precision, durable materials, and artisan-level craftsmanship tailored to your property.',
}) => (
  <section className="relative bg-[#FAF8F5]" data-product-shop>
    {/* Top section number watermark */}
    <div className="section-number right-8 top-8 opacity-[0.025]">01</div>

    <div className="section-py max-w-7xl mx-auto px-6">
      {/* Section header */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16 gap-8 reveal">
        <div>
          <div className="flex items-center gap-3 mb-6">
            <span className="gold-bar" />
            <span className="text-[#C4A84F] text-[11px] tracking-[0.38em] uppercase font-sans font-medium">
              Our Expertise
            </span>
          </div>
          <h2 className="font-serif text-[3.2rem] md:text-[4rem] lg:text-[5rem] text-[#0D0C0B] leading-[0.95] max-w-lg">
            {title}
          </h2>
        </div>
        <div className="max-w-md lg:pb-3">
          <p className="text-[#0D0C0B]/50 text-base leading-relaxed font-sans">
            {subtitle}
          </p>
          <div className="mt-5 flex items-center gap-3">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <i key={i} className="fa-solid fa-star text-[#C4A84F] text-xs" />
              ))}
            </div>
            <span className="text-[#0D0C0B]/40 text-xs font-sans">5.0 Google Rating · Camden County's #1 Fencing Contractor</span>
          </div>
        </div>
      </div>

      {/* Cards — alternating featured card */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E2DDD4] stagger">
        {services.map((service, idx) => (
          <article
            key={service.name}
            className={`group bg-white overflow-hidden reveal card-lift cursor-pointer ${
              idx === 1 ? 'lg:row-span-1' : ''
            }`}
          >
            {/* Image container */}
            <div className="relative h-56 overflow-hidden img-zoom">
              <img
                src={service.image}
                alt={`${service.name} by Masters Fencing St. Mary's Georgia`}
                title={`${service.name} — Masters Fencing`}
                className="w-full h-full object-cover object-center"
              />
              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-[#0D0C0B]/0 group-hover:bg-[#0D0C0B]/55 transition-all duration-500">
                <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-400">
                  <Link
                    to="/services"
                    className="inline-flex items-center gap-2 bg-[#C4A84F] text-[#0D0C0B] text-[11px] font-bold tracking-[0.16em] uppercase px-5 py-2.5 whitespace-nowrap"
                  >
                    View Service <i className="fa-solid fa-arrow-right text-[10px]" />
                  </Link>
                </div>
              </div>

              {/* Tagline pill */}
              <div className="absolute top-4 left-4">
                <span className="bg-[#0D0C0B]/75 backdrop-blur-sm text-[#C4A84F] text-[9px] tracking-[0.2em] uppercase font-sans px-3 py-1.5 border border-[#C4A84F]/30">
                  {service.tagline}
                </span>
              </div>
            </div>

            {/* Content */}
            <div className="p-7">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`fa-solid ${service.icon} text-[#C4A84F] text-sm`} />
                </div>
                <div className="flex-1 h-[1px] bg-[#0D0C0B]/6" />
              </div>
              <h3 className="font-serif text-[1.6rem] text-[#0D0C0B] mb-3 leading-tight">
                {service.name}
              </h3>
              <p className="text-[#0D0C0B]/52 text-sm leading-relaxed mb-5 font-sans">
                {service.description}
              </p>
              <div className="pt-4 border-t border-[#0D0C0B]/6">
                <span className="text-[10px] text-[#C4A84F] tracking-[0.22em] uppercase font-sans font-medium">
                  {service.detail}
                </span>
              </div>
            </div>
          </article>
        ))}
      </div>

      {/* CTA row */}
      <div className="mt-14 flex flex-col sm:flex-row items-center justify-between gap-6 pt-10 border-t border-[#0D0C0B]/8 reveal">
        <p className="font-serif text-2xl text-[#0D0C0B]/65 italic">
          "Unsure which material fits your property? Let's figure it out together."
        </p>
        <a
          href="#estimate"
          className="inline-flex items-center gap-3 bg-[#0D0C0B] text-[#F5F1E8] text-[11px] font-bold tracking-[0.22em] uppercase px-10 py-4 hover:bg-[#C4A84F] hover:text-[#0D0C0B] transition-all duration-300 whitespace-nowrap"
        >
          Get a Free Estimate
          <i className="fa-solid fa-arrow-right text-[10px]" />
        </a>
      </div>
    </div>
  </section>
);

export default ServicesGrid;
